import React from 'react';
import { Target, Users, Zap, Award, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';

const About = () => {
  const { language } = useLanguage();
  const t = translations[language].about;

  const features = [
    {
      icon: Target,
      title: { en: 'Mission Driven', es: 'Impulsados por la Misión', de: 'Missionsgetrieben' },
      description: { 
        en: 'Helping startups and small businesses succeed online',
        es: 'Ayudando a startups y pequeñas empresas a tener éxito en línea',
        de: 'Startups und kleinen Unternehmen zum Online-Erfolg verhelfen',
      },
      color: '#D4AF37',
      gradient: 'from-[#D4AF37] to-[#F4C542]',
    },
    {
      icon: Zap,
      title: { en: 'AI-Powered', es: 'Impulsado por IA', de: 'KI-gestützt' },
      description: {
        en: 'Cutting-edge technology for faster, better results',
        es: 'Tecnología de vanguardia para resultados más rápidos y mejores',
        de: 'Modernste Technologie für schnellere, bessere Ergebnisse',
      },
      color: '#5A6F8F',
      gradient: 'from-[#5A6F8F] to-[#6A7F9F]',
    },
    {
      icon: Users,
      title: { en: 'Client Focused', es: 'Centrado en el Cliente', de: 'Kundenorientiert' },
      description: {
        en: 'Your success is our priority, always',
        es: 'Tu éxito es nuestra prioridad, siempre',
        de: 'Ihr Erfolg ist unsere Priorität, immer',
      },
      color: '#D4AF37',
      gradient: 'from-[#D4AF37] to-[#F4C542]',
    },
    {
      icon: Award,
      title: { en: 'Quality Guaranteed', es: 'Calidad Garantizada', de: 'Qualität garantiert' },
      description: {
        en: 'Premium results without the premium wait time',
        es: 'Resultados premium sin el tiempo de espera premium',
        de: 'Premium-Ergebnisse ohne Premium-Wartezeit',
      },
      color: '#5A6F8F',
      gradient: 'from-[#5A6F8F] to-[#6A7F9F]',
    },
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-[#141619] to-[#0a0c0f] circuit-pattern relative">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
      
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="text-gold-metallic">{t.title.split(' ')[0]}</span> {t.title.split(' ').slice(1).join(' ')}
          </h2>
          <p className="text-xl text-[#5A6F8F] max-w-3xl mx-auto mb-8 font-medium">
            {t.subtitle}
          </p>
          <p className="text-lg text-gray-400 max-w-4xl mx-auto leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="group p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-105 relative overflow-hidden"
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/10 group-hover:to-transparent transition-all duration-300"></div>
                
                <div className="relative z-10">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${feature.gradient} transition-all duration-300 group-hover:scale-110`}
                  >
                    <Icon className="w-6 h-6 text-[#141619]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {feature.title[language]}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {feature.description[language]}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats with Premium Styling */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.stats.map((stat, index) => (
            <div
              key={index}
              className="group p-8 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-105 text-center relative overflow-hidden"
            >
              {/* Animated background on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 via-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/5 group-hover:via-[#D4AF37]/10 group-hover:to-transparent transition-all duration-500"></div>
              
              <div className="relative z-10">
                <div className="text-4xl md:text-5xl font-bold text-gold-metallic mb-3">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wide font-medium">
                  {stat.label}
                </div>
              </div>
              
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Trust Badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-[#1a1d23] to-[#141619] rounded-full border border-[#D4AF37]/30">
            <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-gray-300 font-medium">
              {language === 'en' ? 'Trusted by innovative businesses worldwide' : language === 'es' ? 'Confiado por empresas innovadoras en todo el mundo' : 'Vertraut von innovativen Unternehmen weltweit'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5A6F8F]/30 to-transparent"></div>
    </section>
  );
};

export default About;
